import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ManagedManufacturersApi } from "apis/ManagedManufacturersApi";
import { ManagedStocksApi } from "apis/ManagedStocksApi";
import { BulkEditManagedStock } from "models/daoModels/managedStock/BulkEditManagedStock";
import { ManagedManufacturer } from "models/dbModels/managedManufacturers/ManagedManufacturer";
import { ManagedStock } from "models/dbModels/managedStock/ManagedStock";
import { ManagedManufacturersSearchFilterQuery } from "models/filters/managedManufacturers/ManagedManufacturersSearchFilterQuery";
import { ManagedStockSearchFilterQuery } from "models/filters/managedStocks/ManagedStockSearchFilterQuery";


export const importStockToManufacturers = createAsyncThunk("managedManufacturersSlice/importStockToManufacturer", async (params: BulkEditManagedStock) => {
    const response = await ManagedStocksApi.BulkUpdate(params);
    return response.data;
});

type InitialStateType = {
    id: number;
    name: string;
    modelId: string;
    locked: boolean;
    code: string;
    selectedRows: { position: number, id: number }[][],
    deselectedRows: any[];
    loading: boolean;
    selectAllFilter: boolean;
    keyword: string;
    totalRows: number;
    totalPages: number;
    pageIndex: number;
    pageSize: number;
    error: string;
    table: ManagedManufacturer[];
    manufacturersStockTable: ManagedStock[];
}

const initialState: InitialStateType = {
    id: 0,
    name: "",
    modelId: "",
    locked: false,
    code: "",
    selectedRows: [],
    deselectedRows: [],
    loading: false,
    selectAllFilter: false,
    keyword: "",
    totalRows: 0,
    totalPages: 0,
    pageIndex: 0,
    pageSize: 100,
    error: "",
    table: [],
    manufacturersStockTable: []
}

const storesTablesSlice = createSlice({
    name: "managedManufacturersSlice",
    initialState : {
        ...initialState,
    },
    reducers: {
        cleanManufacturersDetails: (state) => {
            return {
                ...state,
                ...initialState,
            };
        },
        updateManufacturersField: (state, action) => {
            return {
                ...state,
                [action.payload.key]: action.payload.value,
            };
        },
        addSelectedRows: (state, action) => {
            // @ts-ignore
           state.selectedRows[action.payload.index] = action.payload.rows;                 
        },
        addDeselectedRows: (state, action) => {
            // @ts-ignore
            state.deselectedRows[action.payload.index] = action.payload.rows;
        },
        addAGGridSelectedRows: (state, action) => {
            return {
                ...state,
                selectedRows: action.payload
            };
        },
    },
    extraReducers(builder) {

        builder.addCase(getManufacturerDetails.fulfilled, (state, action) => {
            state.id = action.payload.data[0].id;
            state.name = action.payload.data[0].name;
            state.locked = action.payload.data[0].locked;
            state.code = action.payload.data[0].code;
        });

        builder.addCase(getManufacturers.fulfilled, (state, action) => {
            state.table = action.payload.data ? action.payload.data : [];
            state.totalRows = action.payload.totalRows;
            state.totalPages = Math.ceil(Math.ceil(action.payload.totalRows) / state.pageSize);
            state.loading = false;
        });
        builder.addCase(getManufacturers.pending, (state) => {
            state.loading = true;
            state.table = [];
            state.error = "";
        });
        builder.addCase(getManufacturers.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "";
        });
        builder.addCase(getManufacturerStock.fulfilled, (state, action) => {
            state.manufacturersStockTable = action.payload.data ? action.payload.data : [];
        })
    }
});

export const getManufacturers = createAsyncThunk("manufacturersSlice/getManufacturers", async (params?:ManagedManufacturersSearchFilterQuery) => { 
    const response = await ManagedManufacturersApi.Search(params ?? {});
    return response.data;
});

export const getManufacturerStock = createAsyncThunk("manufacturerSlice/getManufacturerStock", async (params?: ManagedStockSearchFilterQuery) => {
    const response = await ManagedStocksApi.OptimsedSearch(params ?? {});
    return response.data;
});

export const getManufacturerDetails = createAsyncThunk("manufacturerSlice/getManufacturerDetails", async (manufacturerIds: number[]) => {
    const response = await ManagedManufacturersApi.Search({ ids: manufacturerIds });
    return response.data;
});

export const createManufacturer = createAsyncThunk("manufacturerSlice/createManufacturer", async (manufacturer: ManagedManufacturer) => {
    const response = await ManagedManufacturersApi.Add([manufacturer]);
    return response.data;
});

export const createManufacturers = createAsyncThunk("manufacturerSlice/createManufacturers", async (manufacturer: ManagedManufacturer[]) => {
    const response = await ManagedManufacturersApi.Add(manufacturer);
    return response.data;
});

export const updateManufacturer = createAsyncThunk("manufacturerSlice/updateManufacturer", async (manufacturer: ManagedManufacturer) => {
    const response = await ManagedManufacturersApi.Update([manufacturer]);
    return response.data;
});

export const updateManufacturers = createAsyncThunk("manufacturerSlice/updateManufacturers", async (manufacturer: ManagedManufacturer[]) => {
    const response = await ManagedManufacturersApi.Update(manufacturer);
    return response.data;
});

export const deleteManufacturers = createAsyncThunk("manufacturerSlice/deleteManufacturers", async (params: ManagedManufacturersSearchFilterQuery) => {
    const response = await ManagedManufacturersApi.Delete(params);
    return response.data;
});

export const { addDeselectedRows, addSelectedRows, cleanManufacturersDetails, updateManufacturersField, addAGGridSelectedRows } = storesTablesSlice.actions;

export default storesTablesSlice.reducer;